// Agent identity hues: one colour per agent, the same on every screen,
// desktop and companion, for every viewer, in every theme.
//
// The hue is a PURE function of the agent's template - never of its
// display name, its position in a roster, or which agents the viewer can
// see - so the operator's roster, a child's companion (which only sees
// its own agent) and an avatar rendered from a bare template all agree,
// and adding or removing an agent recolours nobody.
//
// The catalogue's own templates (the blueprint ids nearly every customer
// uses) have fixed slots in catalogue order, so a standard team is always
// distinct and matches the guide's mock (Julia/assistant = id-1 …
// David/accounting = id-6). Any other template hashes onto the eight;
// two of those can share a colour, and the initial and name tell them
// apart.
//
// The palette is eight theme-independent CSS variables, --id-1 … --id-8
// (docs/ui-guide/tokens.css), defined by the theme system. The hex values
// below are ONLY the var() fallback so an avatar still renders before
// (or without) those variables - this module is the one sanctioned place
// for identity colour literals.
//
// No `@/` imports so it bundles standalone.

export const IDENTITY_HUE_COUNT = 8;

const FALLBACK = ["#8b7cf6", "#e26fb4", "#5b9cf0", "#e0736a", "#e59a45", "#4fb58a", "#3fb5c9", "#b5a642"] as const;

// services/agent_blueprints/catalog.py, in catalogue order.
const CATALOGUE_HUES: Record<string, number> = {
  assistant: 1, research: 2, trading: 3, lawyer: 4, trainer: 5, accounting: 6, expert: 7,
};

/** FNV-1a over UTF-16 units - small, stable across browsers and sessions.
 * Exported for anything else that needs a stable order or key from a
 * string (the Memory map's ring order, its "seen" set). */
export function stableHash(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The identity hue 1…8 of an agent template. Pure: same template, same hue, everywhere. */
export function hueForKey(template: string | null | undefined): number {
  const key = (template ?? "").trim().toLowerCase();
  return CATALOGUE_HUES[key] ?? (stableHash(key) % IDENTITY_HUE_COUNT) + 1;
}

/** Alias under the name the primitives plan uses. */
export const agentHue = hueForKey;

/** CSS colour for a hue index: `var(--id-N, <fallback>)`. */
export function identityColor(hue: number | null | undefined): string {
  const n = Number.isFinite(hue) ? Math.min(IDENTITY_HUE_COUNT, Math.max(1, Math.round(hue as number))) : 1;
  return `var(--id-${n}, ${FALLBACK[n - 1]})`;
}
